import { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  EMAIL_DELIVERY_STATUSES,
  EMAIL_TEMPLATE_KEYS,
  LOCALES,
  browserTimezone,
  endOfDayIso,
  maskEmails,
  startOfDayIso,
  type AdminEmailDeliveryQuery,
  type AdminEmailDeliveryRowDto,
  type EmailDeliveryStatus,
} from '@icebreaker/shared';
import { emailTemplateLabelKey, type TranslationKey } from '@icebreaker/shared/i18n';
import {
  Button,
  DateTimePicker,
  EmptyState,
  ErrorBox,
  Field,
  Select,
  Skeleton,
  Table,
  Tag,
  type TagVariant,
  tableStyles,
} from '@icebreaker/ui';
import { ROUTES } from '@/app/routes';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  emailDeliveryFilterChanged,
  emailDeliveryFiltersReset,
  fetchEmailDeliveries,
  INITIAL_EMAIL_DELIVERY_FILTERS,
  type EmailDeliveryFilters,
} from '../store/admin-email-deliveries.slice';
import { EmailDeliveryRecipient } from './email-delivery-recipient';
import styles from './admin.module.css';

const PAGE_SIZE = 50;

/**
 * E-POSTA GÖNDERİM GÖRÜNÜMÜ — SALT OKUNUR.
 *
 * TASARIM REFERANSI YOKTUR: devir paketinde panelin yalnız kullanıcı,
 * moderasyon ve raporlama ekranları çizilidir; gönderim defteri hiç geçmez.
 * Ekran bu yüzden panelin KENDİ desenini izler (kullanıcılar ekranıyla aynı
 * başlık satırı, aynı araç çubuğu, aynı tablo ve sayfalama) ve tüm değerler
 * `--ib-*` token'larından gelir.
 *
 * EKRANIN SINIRI METİNDE YAZILIDIR: gövde saklanmaz, adres saklanmaz, yeniden
 * gönderim yoktur. Olmayan yeteneği personel aramasın diye ekran bunu söyler.
 */
export function EmailDeliveriesScreen() {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const state = useAppSelector((root: RootState) => root.adminEmailDeliveries);
  const { filters } = state;

  const refresh = useCallback(() => {
    dispatch(fetchEmailDeliveries.request(toQuery(filters)));
  }, [dispatch, filters]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Çevrimdışı dönüşte otomatik toparlanma — panelin diğer listeleriyle aynı desen.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onOnline = () => refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refresh]);

  const narrowed = isNarrowed(filters);
  const pageCount = Math.max(1, Math.ceil(state.total / PAGE_SIZE));
  // Başlık sayacı YALNIZ gerçek veriden: ilk yüklemede "0 kayıt" DEMEZ.
  const countReady = state.phase === 'succeeded';

  return (
    <section className={styles.screen}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{t.t('admin.emailDeliveries.title')}</h1>
        {countReady ? (
          <span className={styles.titleMeta}>
            {t.t('admin.emailDeliveries.headline', { count: state.total })}
          </span>
        ) : state.phase === 'loading' ? (
          <span className={styles.titleMeta}>{t.t('a11y.loadingContent')}</span>
        ) : null}
      </div>
      <p className={styles.lead}>{t.t('admin.emailDeliveries.lead')}</p>

      <div
        className={styles.filterGrid}
        role="group"
        aria-label={t.t('admin.emailDeliveries.filters.heading')}
      >
        <FilterSelect
          id="ed-status"
          label={t.t('admin.emailDeliveries.filters.status')}
          value={filters.status}
          options={EMAIL_DELIVERY_STATUSES.map((status) => ({
            value: status,
            label: t.t(`admin.emailDeliveries.statuses.${status}` as TranslationKey),
          }))}
          allLabel={t.t('admin.emailDeliveries.filters.all')}
          onChange={(value) =>
            dispatch(
              emailDeliveryFilterChanged({ status: value as EmailDeliveryFilters['status'] }),
            )
          }
        />
        <FilterSelect
          id="ed-template"
          label={t.t('admin.emailDeliveries.filters.template')}
          value={filters.template}
          options={EMAIL_TEMPLATE_KEYS.map((template) => ({
            value: template,
            label: t.t(emailTemplateLabelKey(template)),
          }))}
          allLabel={t.t('admin.emailDeliveries.filters.all')}
          onChange={(value) =>
            dispatch(
              emailDeliveryFilterChanged({ template: value as EmailDeliveryFilters['template'] }),
            )
          }
        />
        <FilterSelect
          id="ed-locale"
          label={t.t('admin.emailDeliveries.filters.locale')}
          value={filters.locale}
          options={LOCALES.map((locale) => ({
            value: locale,
            label: t.t(`language.${locale}` as TranslationKey),
          }))}
          allLabel={t.t('admin.emailDeliveries.filters.all')}
          onChange={(value) =>
            dispatch(
              emailDeliveryFilterChanged({ locale: value as EmailDeliveryFilters['locale'] }),
            )
          }
        />
        {/*
          TARİH ALANLARI ÜRÜNÜN KENDİ SEÇİCİSİYLE: yerel `<input type="date">`
          yasaktır (`native-date-input.guard`) — takvim işletim sistemine göre
          değişir, TR biçimi ve hafta başlangıcı garanti değildir. Filtre
          SAATSİZDİR; `YYYY-MM-DD` değeri sunucuya PERSONELİN dilimindeki gün
          başı/gün sonu anına çevrilerek gider (`toQuery`).
        */}
        <div className={styles.filterCell}>
          <Field id="ed-from" label={t.t('admin.emailDeliveries.filters.from')}>
            <DateTimePicker
              id="ed-from"
              mode="date"
              clearable
              value={filters.from}
              onChange={(value) => dispatch(emailDeliveryFilterChanged({ from: value }))}
            />
          </Field>
        </div>
        <div className={styles.filterCell}>
          <Field id="ed-to" label={t.t('admin.emailDeliveries.filters.to')}>
            <DateTimePicker
              id="ed-to"
              mode="date"
              clearable
              value={filters.to}
              onChange={(value) => dispatch(emailDeliveryFilterChanged({ to: value }))}
            />
          </Field>
        </div>
        {narrowed ? (
          <div className={styles.filterCell}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => dispatch(emailDeliveryFiltersReset())}
            >
              {t.t('admin.emailDeliveries.filters.reset')}
            </Button>
          </div>
        ) : null}
      </div>

      <ErrorBox error={state.error} className={styles.errorBox}>
        <Button variant="secondary" size="sm" onClick={refresh}>
          {t.t('common.retry')}
        </Button>
      </ErrorBox>

      <div className={styles.tableWrap}>
        {state.phase === 'loading' && state.items.length === 0 ? (
          <Skeleton label={t.t('admin.emailDeliveries.loading')} lines={6} />
        ) : state.items.length === 0 && state.phase === 'succeeded' ? (
          <EmptyState
            title={t.t(
              narrowed ? 'admin.emailDeliveries.emptyTitle' : 'admin.emailDeliveries.emptyAllTitle',
            )}
            body={t.t(
              narrowed ? 'admin.emailDeliveries.emptyBody' : 'admin.emailDeliveries.emptyAllBody',
            )}
            /*
              ADIMLAR ve TEK DOLU EYLEM yalnız DARALTMA varken çizilir: süzgeçsiz
              boş liste "hiç e-posta gitmemiş" demektir ve orada basılacak bir
              düğme yoktur — uydurma eylem konmaz (README 4.7).
            */
            steps={
              narrowed
                ? [
                    { title: t.t('admin.emailDeliveries.emptyStepReset') },
                    { title: t.t('admin.emailDeliveries.emptyStepRange') },
                  ]
                : undefined
            }
            action={
              narrowed ? (
                <Button variant="primary" onClick={() => dispatch(emailDeliveryFiltersReset())}>
                  {t.t('admin.emailDeliveries.filters.reset')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          /*
            ERİŞİLEBİLİR AD: tablonun kendi başlığı yok, ekran başlığı onun
            adıdır. Panelin tablolarında bu ad GENEL OLARAK eksiktir (ayrı
            tur · `docs/tur-kuyrugu.md`) ama YENİ yüzey o borca eklenmez;
            yeni anahtar da açılmaz, var olan ekran başlığı kullanılır.
          */
          <Table aria-label={t.t('admin.emailDeliveries.title')}>
            <thead>
              <tr>
                <th>{t.t('admin.emailDeliveries.table.template')}</th>
                <th>{t.t('admin.emailDeliveries.table.recipient')}</th>
                <th>{t.t('admin.emailDeliveries.table.locale')}</th>
                <th>{t.t('admin.emailDeliveries.table.status')}</th>
                <th>{t.t('admin.emailDeliveries.table.queued')}</th>
                <th>{t.t('admin.emailDeliveries.table.result')}</th>
                <th>{t.t('admin.emailDeliveries.table.reason')}</th>
              </tr>
            </thead>
            <tbody>
              {state.items.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className={styles.personName}>
                      {t.t(emailTemplateLabelKey(row.template))}
                    </span>
                  </td>
                  <td>
                    <EmailDeliveryRecipient
                      hashPrefix={row.toEmailHashPrefix}
                      userLink={
                        row.relatedUserId
                          ? `${ROUTES.users}?q=${encodeURIComponent(row.relatedUserId)}`
                          : null
                      }
                      LinkComponent={Link}
                    />
                  </td>
                  <td className={tableStyles.cellMuted}>
                    {t.t(`language.${row.locale}` as TranslationKey)}
                  </td>
                  <td>
                    <span className={styles.statusCell}>
                      <Tag variant={statusVariant(row.status)}>
                        {t.t(`admin.emailDeliveries.statuses.${row.status}` as TranslationKey)}
                      </Tag>
                      {row.attemptCount > 0 ? (
                        <span className={styles.cellMeta}>
                          {t.t('admin.emailDeliveries.attempts', { count: row.attemptCount })}
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className={styles.cellMeta}>
                    {t.formatDate(row.queuedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  </td>
                  <td className={styles.cellMeta}>{resultText(t, row)}</td>
                  <td className={styles.reasonCell}>
                    {/*
                      MASKELEME İKİNCİ KEZ BURADA — AMA BU "SAVUNMA DERİNLİĞİ"
                      DEĞİLDİR ve öyle okunmamalıdır (QA, 16.09.2026).
                      İki geçiş de AYNI `maskEmails` fonksiyonundan geçer: o
                      fonksiyonda bir kaçamak varsa İKİ KAPI DA AYNI ANDA düşer.
                      Bu geçişin gerçekten kapsadığı tek durum SUNUCU TARAFI
                      BAĞLANTI HATASIDIR: yeni bir uç, önbellekten dönen eski bir
                      yanıt ya da maskelemeyi atlayan bir DTO yolu. Maskelemenin
                      KENDİSİNİ koruyan şey bu satır değil, normalleştirme
                      kapısıdır (`packages/shared/src/domain/email-mask.spec.ts` —
                      adım söküldüğünde kırılan mutasyon probu).
                      İkinci bir maskeleme KURALI bilerek yazılmaz: iki kural
                      zamanla ayrışır ve hangisinin doğru olduğu bilinemez.
                    */}
                    {row.status === 'failed' || row.status === 'dead' ? (
                      <span className={styles.reasonText}>
                        {maskEmails(row.lastError) ?? t.t('admin.emailDeliveries.reasonNone')}
                      </span>
                    ) : (
                      <span className={styles.cellMeta}>—</span>
                    )}
                    {row.providerMessageId ? (
                      /*
                        İZ NUMARASI MASKELENMEZ (QA ölçümü, 16.09.2026): RFC 5322
                        Message-ID yapısı gereği `<benzersiz@gönderen-sunucu>`dur;
                        üretimde 342 satırın 340'ı bu biçimde ve maskeleme ekranın
                        tek iz sürme aracını `<[eposta]>`ye çeviriyordu. Alan
                        alıcının adresini taşımaz — gerekçe sunucu tarafında
                        `admin-email-deliveries.service.ts`te ölçümle yazılıdır.
                      */
                      <span className={styles.cellMeta}>
                        {t.t('admin.emailDeliveries.providerMessageId', {
                          id: row.providerMessageId,
                        })}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {pageCount > 1 ? (
        <div className={styles.pager}>
          <Button
            variant="secondary"
            size="sm"
            disabled={filters.page <= 1}
            onClick={() =>
              dispatch(emailDeliveryFilterChanged({ page: Math.max(1, filters.page - 1) }))
            }
          >
            {t.t('admin.overview.prevPage')}
          </Button>
          <span className={styles.pagerStatus} role="status">
            {t.t('admin.overview.pageStatus', {
              page: t.formatNumber(filters.page),
              pageCount: t.formatNumber(pageCount),
            })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={!state.hasNext}
            onClick={() => dispatch(emailDeliveryFilterChanged({ page: filters.page + 1 }))}
          >
            {t.t('admin.overview.nextPage')}
          </Button>
        </div>
      ) : null}

      <p className={styles.maskNote}>{t.t('admin.emailDeliveries.maskNote')}</p>
    </section>
  );
}

/**
 * Ekran süzgeçleri → sunucu sorgusu. `'all'` alanı sorguya HİÇ girmez.
 *
 * TARİH GİRDİSİ GÜNDÜR, SUNUCU ANI İSTER — VE GÜN PERSONELİN GÜNÜDÜR.
 * Eski hâli yerel güne doğrudan `Z` ekliyordu (`${gun}T00:00:00.000Z`); o dize
 * UTC gününün başıdır. Europe/Istanbul'da (UTC+3, ana pazar) ölçüldü:
 * "Başlangıç: bugün" bugün 01:30'daki satırı GETİRMİYOR, "Bitiş: bugün" yarın
 * 01:00'daki satırı GETİRİYORDU. Çevrim artık `startOfDayIso`/`endOfDayIso`
 * üzerinden, seçicinin ürettiği duvar saatiyle AYNI dilim tabanında yapılır.
 *
 * `timezone` parametrelidir ve varsayılanı tarayıcının dilimidir: testin üç
 * ayrı dilimde ölçebilmesi için (UTC · UTC+3 · UTC-5) tek yol budur.
 */
export function toQuery(
  filters: EmailDeliveryFilters,
  timezone: string = browserTimezone(),
): AdminEmailDeliveryQuery {
  const from = filters.from ? startOfDayIso(filters.from, timezone) : '';
  const to = filters.to ? endOfDayIso(filters.to, timezone) : '';
  return {
    ...(filters.status === 'all' ? {} : { status: filters.status }),
    ...(filters.template === 'all' ? {} : { template: filters.template }),
    ...(filters.locale === 'all' ? {} : { locale: filters.locale }),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    page: filters.page,
    pageSize: PAGE_SIZE,
  };
}

function isNarrowed(filters: EmailDeliveryFilters): boolean {
  return (
    filters.status !== INITIAL_EMAIL_DELIVERY_FILTERS.status ||
    filters.template !== INITIAL_EMAIL_DELIVERY_FILTERS.template ||
    filters.locale !== INITIAL_EMAIL_DELIVERY_FILTERS.locale ||
    filters.from !== '' ||
    filters.to !== ''
  );
}

function statusVariant(status: EmailDeliveryStatus): TagVariant {
  if (status === 'sent') return 'success';
  if (status === 'failed') return 'warning';
  if (status === 'dead') return 'danger';
  return 'outline';
}

function resultText(t: ReturnType<typeof useTranslation>, row: AdminEmailDeliveryRowDto): string {
  const stamp = row.sentAt ?? row.failedAt;
  if (!stamp) return t.t('admin.emailDeliveries.notYet');
  return t.formatDate(stamp, { dateStyle: 'medium', timeStyle: 'short' });
}

interface FilterSelectProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly allLabel: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly onChange: (value: string) => void;
}

function FilterSelect({ id, label, value, allLabel, options, onChange }: FilterSelectProps) {
  return (
    <div className={styles.filterCell}>
      <label className={styles.filterLabel} htmlFor={id}>
        {label}
      </label>
      <Select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="all">{allLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}
